import React, { useEffect } from 'react'
import { Form, Input, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Modal } from '~/Components/Modal'
import { PasswordInput } from '~/Components/Forms'
import { UserService } from '~/Services'

interface ChangePasswordProps {
  visible: boolean
  onClose: () => void
}

interface ChangePasswordFormValues {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({
  visible,
  onClose,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [loading, setLoading] = React.useState(false)

  useEffect(() => {
    if (visible) {
      form.resetFields()
    }
  }, [visible, form])

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    try {
      setLoading(true)
      
      await UserService.Put.changePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      })

      message.success(t('profile.changePasswordSuccess'))
      form.resetFields()
      onClose()
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('profile.changePasswordFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={t('profile.changePassword')}
      visible={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={500}
      okText={t('profile.changePasswordBtn')}
      cancelText={t('cancel')}
      loadingData={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label={t('profile.currentPassword')}
          name="currentPassword"
          rules={[
            { required: true, message: t('profile.currentPasswordRequired') },
          ]}
        >
          <PasswordInput
            placeholder={t('profile.enterCurrentPassword')}
            showGenerator={false}
            showCopy={false}
          />
        </Form.Item>

        <Form.Item
          label={t('profile.newPassword')}
          name="newPassword"
          rules={[
            { required: true, message: t('profile.newPasswordRequired') },
            { min: 6, message: t('passwordMinLength') },
          ]}
        >
          <PasswordInput
            placeholder={t('profile.enterNewPassword')}
            onGenerate={(password) => {
              form.setFieldsValue({
                newPassword: password,
                confirmPassword: password,
              })
              message.success(t('passwordGenerated'))
            }}
          />
        </Form.Item>

        <Form.Item
          label={t('profile.confirmNewPassword')}
          name="confirmPassword"
          dependencies={['newPassword']}
          hasFeedback
          rules={[
            { required: true, message: t('profile.confirmNewPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const newPassword = getFieldValue('newPassword')
                if (!value || newPassword === value) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  new Error(t('profile.passwordNotMatch')),
                )
              },
            }),
          ]}
        >
          <PasswordInput
            placeholder={t('profile.reEnterNewPassword')}
            showGenerator={false}
            showCopy={false}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}